import { AlertSidebar } from '@/components/alert-sidebar';
import BulkEditSidebar from '@/components/bulk-edit-sidebar';
import { CompareOffersSidebar } from '@/components/compare-offers-sidebar';
import FilterSidebar from '@/components/filter-sidebar';
import FloatingActionMenu from '@/components/floating-action-menu';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import {
  DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
  filterSidebarAccordionDefaultValueState,
} from '@/states/filter-sidebar-accordion.state';
import { fullscreenState } from '@/states/fullscreen.state';
import { isSidebarAlertOpenState } from '@/states/is-sidebar-alert-open.state';
import { isSidebarBulkEditOpenState } from '@/states/is-sidebar-bulk-edit-open.state';
import { isSidebarCompareOpen } from '@/states/is-sidebar-compare-open.state';
import { isSidebarFilterOpenState } from '@/states/is-sidebar-filter-open.state';
import { productsState } from '@/states/products.state';
import { shouldShowFloatingActionMenuState } from '@/states/should-show-floating-action-menu.state';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AnimatePresence } from 'motion/react';

import { useDealContext } from '../lib/contexts/deal.context';
import Table from './table/table';
import { TableHeader } from './table/table-header';

export type DealItemVersionLocaleLabel = DealItemWithVersions & {
  localeLabel?: string;
};

export const ProductView = () => {
  const dealId = useDealContext().deal.id;

  const [showFilterSidebar, setShowFilterSidebar] = useAtom(
    isSidebarFilterOpenState
  );
  const setFilterSidebarAccordionDefault = useSetAtom(
    filterSidebarAccordionDefaultValueState
  );
  const [showAlertSidebar, setShowAlertSidebar] = useAtom(
    isSidebarAlertOpenState
  );
  const [showCompareSidebar, setShowCompareSidebar] =
    useAtom(isSidebarCompareOpen);

  const shouldShowFloatingMenu = useAtomValue(
    shouldShowFloatingActionMenuState
  );
  const [isBulkEditSidebarOpen, setIsBulkEditSidebarOpen] = useAtom(
    isSidebarBulkEditOpenState
  );
  const fullscreen = useAtomValue(fullscreenState);
  const products = useAtomValue(productsState);

  return (
    <div
      className={cn(
        'flex flex-col',
        fullscreen
          ? 'h-screen bg-white'
          : 'h-[calc(100vh-var(--nav-height,0px))] bg-gray-50'
      )}
    >
      <div
        className={cn(
          'sticky z-table-header shrink-0 bg-white',
          fullscreen ? 'top-0' : 'top-[var(--nav-height,0px)]'
        )}
      >
        <TableHeader />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Table />
      </div>

      {/* Menu flottant d'actions - se cache quand la sidebar est ouverte */}
      {shouldShowFloatingMenu && !isBulkEditSidebarOpen && (
        <FloatingActionMenu
          onOpenEditSidebar={() => {
            setIsBulkEditSidebarOpen(true);
          }}
        />
      )}

      {/* Sidebars */}
      <AnimatePresence>
        {isBulkEditSidebarOpen && (
          <BulkEditSidebar
            key="bulk-edit-sidebar"
            onClose={() => setIsBulkEditSidebarOpen(false)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showFilterSidebar && (
          <FilterSidebar
            key="filter-sidebar"
            onClose={() => {
              setShowFilterSidebar(false);
              setFilterSidebarAccordionDefault(
                DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES
              );
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAlertSidebar && (
          <AlertSidebar
            key="alert-sidebar"
            onClose={() => setShowAlertSidebar(false)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCompareSidebar && (
          <CompareOffersSidebar
            key="compare-offers-sidebar"
            dealId={dealId}
            onClose={() => setShowCompareSidebar(false)}
            products={products}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
